import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../../firebase';
import {
  doc,
  getDoc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp,
  increment,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdoptNeed.css';
import { trackEvent } from "../../utils/analytics";
import ImpactNetworkAttributionSelector from "../../components/ImpactNetworkAttributionSelector";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a1a',
      fontFamily: '-apple-system, sans-serif',
      '::placeholder': { color: '#aaa' }
    },
    invalid: { color: '#dc2626' }
  }
};

function calculateNextBillingDate(preferredDeliveryDay, frequency) {
  if (frequency === 'one-time') return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const safeDeliveryDay = Math.max(1, Math.min(28, Number(preferredDeliveryDay) || 1));
  const billingDay = Math.max(1, safeDeliveryDay - 5);

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const thisMonthBilling = new Date(Date.UTC(currentYear, currentMonth, billingDay, 12, 0, 0));

  if (today <= new Date(currentYear, currentMonth, billingDay)) {
    return thisMonthBilling;
  }

  return new Date(Date.UTC(currentYear, currentMonth + 1, 1, 12, 0, 0));
}

function AdoptNeedForm({ need }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(false);
  const [error, setError] = useState('');
  const [cardOnFile, setCardOnFile] = useState(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);

  const [networkAttribution, setNetworkAttribution] = useState({
    networkId: null,
    networkName: null,
  });

  const priorityColors = {
    critical: { bg: '#fee2e2', color: '#991b1b' },
    core: { bg: '#fef3c7', color: '#92400e' },
    supplemental: { bg: '#e0f2fe', color: '#075985' }
  };

  useEffect(() => {
    async function checkCardOnFile() {
      try {
        const donorDoc = await getDoc(doc(db, 'donors', auth.currentUser.uid));
        const data = donorDoc.data();

        if (data?.cardLast4) {
          setCardOnFile({ last4: data.cardLast4, brand: data.cardBrand });
        } else {
          setShowNewCard(true);
        }
      } catch (err) {
        console.error('Error checking card:', err);
        setShowNewCard(true);
      } finally {
        setLoadingCard(false);
      }
    }

    checkCardOnFile();
  }, []);

  async function handleAdopt() {
    try {
      setError('');
      setAdopting(true);

      const freshNeedDoc = await getDoc(doc(db, 'needs', need.id));
      const freshNeed = freshNeedDoc.data();

      if (freshNeed.status === 'funded') {
        setError('This need was just adopted by another donor. Please go back and choose another need.');
        setAdopting(false);
        return;
      }

      const existingQ = query(
        collection(db, 'subscriptions'),
        where('donorId', '==', auth.currentUser.uid),
        where('needId', '==', need.id),
        where('status', '==', 'active')
      );

      const existingSnap = await getDocs(existingQ);

      if (!existingSnap.empty) {
        setError('You are already sponsoring this need. Visit your dashboard to manage your commitments.');
        setAdopting(false);
        return;
      }

      if (showNewCard || !cardOnFile) {
        if (!stripe || !elements) {
          setError('Payment system not ready. Please try again.');
          setAdopting(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);

        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: `${userProfile.firstName} ${userProfile.lastName}`,
            email: userProfile.email
          }
        });

        if (stripeError) {
          setError(stripeError.message);
          setAdopting(false);
          return;
        }

        const functions = getFunctions();
        const savePaymentMethod = httpsCallable(functions, 'savePaymentMethod');
        const result = await savePaymentMethod({ paymentMethodId: paymentMethod.id });

        if (!result.data.success) {
          setError('Failed to save payment method. Please try again.');
          setAdopting(false);
          return;
        }
      }

      await addDoc(collection(db, 'subscriptions'), {
        donorId: auth.currentUser.uid,
        donorName: `${userProfile.firstName} ${userProfile.lastName}`,
        donorFirstName: userProfile.firstName,
        donorLastInitial: userProfile.lastName?.charAt(0) || '',
        donorCity: userProfile.city,
        donorState: userProfile.state,
        donorAnonymity: userProfile.anonymityPreference,

        orgId: need.orgId,
        orgName: need.orgName,

        needId: need.id,
        itemName: need.itemName,
        frequency: need.frequency,
        pricePerCycle: need.pricePerCycle,
        impactQuantity: need.impactQuantity,
        impactUnit: need.impactUnit,
        impactStatement: need.impactStatement,
        quantityPerCycle: need.quantityPerCycle || 1,
        vendorName: need.vendorName || '',
        vendorProductUrl: need.vendorProductUrl || '',

        networkId: networkAttribution.networkId || null,
        networkName: networkAttribution.networkName || null,

        status: 'active',
        createdAt: serverTimestamp(),
        nextBillingDate: calculateNextBillingDate(
          need.preferredDeliveryDay,
          need.frequency
        ),
        preferredDeliveryDay: need.preferredDeliveryDay,
        totalDeliveries: 0,
        totalImpact: 0
      });

      

      await updateDoc(doc(db, 'needs', need.id), {
        sponsorCount: increment(1),
        status: 'funded'
      });

      trackEvent("commitment_created", {
        need_id: need.id,
        item_name: need.itemName,
        org_name: need.orgName,
        value: need.pricePerCycle,
        currency: "USD",
        flow: "direct_adopt",
        network_id: networkAttribution.networkId || null,
        network_name: networkAttribution.networkName || null,
      });

      setAdopted(true);
    } catch (err) {
      console.error('Error adopting need:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setAdopting(false);
    }
  }

  if (adopted) {
    return (
      <div className="adopt-page">
        <div className="adopt-success">
          <div className="success-icon">✓</div>
          <h1>You are now responsible for this need.</h1>
          <p className="success-org">{need.orgName}</p>
          <div className="success-item">{need.itemName}</div>
          <p className="success-impact">{need.impactStatement}</p>

          <div className="success-details">
            <div className="success-detail">
              <span className="detail-label">Your commitment</span>
              <span className="detail-value">
                ${Math.ceil(need.pricePerCycle)}/{need.frequency}
              </span>
            </div>

            {networkAttribution.networkName && (
              <div className="success-detail">
                <span className="detail-label">Impact Network</span>
                <span className="detail-value">
                  {networkAttribution.networkName}
                </span>
              </div>
            )}

            <div className="success-detail">
              <span className="detail-label">First delivery</span>
              <span className="detail-value">Within 3-5 days</span>
            </div>

            <div className="success-detail">
              <span className="detail-label">Your name on the box</span>
              <span className="detail-value">
                {userProfile.anonymityPreference === 'anonymous'
                  ? 'A generous donor from Springfield'
                  : userProfile.anonymityPreference === 'fullName'
                  ? `${userProfile.firstName} ${userProfile.lastName}`
                  : `${userProfile.firstName} ${userProfile.lastName?.charAt(0)}.`
                }
              </span>
            </div>
          </div>

          <p className="success-note">
            You will receive a confirmation email shortly.
            Every time an order ships, we will let you know.
            Every time the box arrives, we will send you proof.
          </p>

          <div className="success-actions">
            <button className="btn-primary" onClick={() => navigate('/give/dashboard')}>
              View my commitments
            </button>
            <button className="btn-secondary" onClick={() => navigate('/give/browse')}>
              Browse more needs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="adopt-page">
      <div className="adopt-card">

        <div className="adopt-logo" onClick={() => navigate('/')}>
          somebody.gives
        </div>

        <div className="adopt-back" onClick={() => navigate('/give/browse')}>
          ← Back to browse
        </div>

        <div className="adopt-need-summary">
          <div className="adopt-org">{need.orgName}</div>
          <h1 className="adopt-item">{need.itemName}</h1>
          <p className="adopt-description">{need.description}</p>

          <div className="adopt-badges">
            <span
              className="adopt-badge"
              style={{
                background: priorityColors[need.priority]?.bg,
                color: priorityColors[need.priority]?.color
              }}
            >
              {need.priority}
            </span>
            <span className="adopt-freq">{need.frequency}</span>
          </div>

          <div className="adopt-impact-box">
            <div className="impact-label">Your impact</div>
            <div className="impact-statement">{need.impactStatement}</div>
          </div>
        </div>

        <div className="adopt-commitment">
          <div className="commitment-label">Your commitment</div>
          <div className="commitment-amount">
            ${Math.ceil(need.pricePerCycle)}
            <span>/{need.frequency}</span>
          </div>
          <div className="commitment-note">
            Billed automatically. Cancel anytime.
          </div>
        </div>

        <ImpactNetworkAttributionSelector
          selectedNetworkId={networkAttribution.networkId}
          onChange={setNetworkAttribution}
        />

        <div className="adopt-recognition">
          <div className="recognition-label">
            Your name on every gift card insert
          </div>
          <div className="recognition-name">
            {userProfile?.anonymityPreference === 'anonymous'
              ? 'A generous donor from Springfield'
              : userProfile?.anonymityPreference === 'fullName'
              ? `${userProfile?.firstName} ${userProfile?.lastName}`
              : `${userProfile?.firstName} ${userProfile?.lastName?.charAt(0)}.`
            }
            {userProfile?.anonymityPreference !== 'anonymous' &&
              `, ${userProfile?.city} ${userProfile?.state}`}
          </div>
          <div className="recognition-change">
            Change in account settings
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          {loadingCard ? (
            <div
              style={{
                fontSize: '0.9rem',
                color: '#888',
                marginBottom: '1rem'
              }}
            >
              Loading payment info...
            </div>
          ) : cardOnFile && !showNewCard ? (
            <div
              style={{
                background: '#f5f5f5',
                borderRadius: '8px',
                padding: '0.875rem 1rem',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span style={{ fontSize: '0.9rem', color: '#333' }}>
                {cardOnFile.brand.charAt(0).toUpperCase() +
                  cardOnFile.brand.slice(1)} ending in {cardOnFile.last4}
              </span>
              <button
                onClick={() => setShowNewCard(true)}
                style={{
                  background: 'transparent',
                  color: '#2d6a4f',
                  border: 'none',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <label
                style={{
                  fontSize: '0.85rem',
                  color: '#555',
                  display: 'block',
                  marginBottom: '0.5rem'
                }}
              >
                Card details
              </label>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '0.875rem 1rem',
                  background: 'white'
                }}
              >
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
              {cardOnFile && (
                <button
                  onClick={() => setShowNewCard(false)}
                  style={{
                    background: 'transparent',
                    color: '#888',
                    border: 'none',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    marginTop: '0.5rem'
                  }}
                >
                  ← Use saved card
                </button>
              )}
            </div>
          )}
        </div>

        {error && <div className="adopt-error">{error}</div>}

        <button
          className="btn-adopt-confirm"
          onClick={handleAdopt}
          disabled={adopting}
        >
          {adopting
            ? 'Setting up your commitment...'
            : `Take responsibility — $${Math.ceil(need.pricePerCycle)}/${need.frequency}`
          }
        </button>

        <p className="adopt-terms">
          By confirming you authorize recurring charges at the
          frequency above. You can cancel anytime from your dashboard.
          A small platform fee is included in the price shown.
        </p>

      </div>
    </div>
  );
}

function AdoptNeed() {
  const { needId } = useParams();
  const [need, setNeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchNeed() {
      try {
        const needDoc = await getDoc(doc(db, 'needs', needId));

        if (needDoc.exists()) {
          setNeed({ id: needDoc.id, ...needDoc.data() });
        }
      } catch (err) {
        console.error('Error fetching need:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchNeed();
  }, [needId]);

  if (loading) return <div className="adopt-loading">Loading...</div>;

  if (!need) {
    return (
      <div className="adopt-loading">
        Need not found.{' '}
        <span onClick={() => navigate('/give/browse')}>Browse other needs</span>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <AdoptNeedForm need={need} />
    </Elements>
  );
}

export default AdoptNeed;